import React, { PureComponent } from 'react';
import './ResultsChart.css';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  Tooltip,
  YAxis,
  Legend,
  CartesianGrid,
} from 'recharts';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';
import { NumericFormat } from 'react-number-format';

/**
 * Minimal chart:
 * - Hide Y-axis labels
 * - Show no horizontal grid lines
 * - Show both "portfolio_value" and "cash" in tooltip (cash is tooltip-only, not plotted)
 */

export const getPortfolioValueDomain = (rows) => {
  const values = (Array.isArray(rows) ? rows : [])
    .map((row) => row?.portfolio_value)
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (values.length === 0) {
    return [0, 100];
  }

  let minVal = values.reduce((min, value) => Math.min(min, value), Infinity);
  let maxVal = values.reduce((max, value) => Math.max(max, value), -Infinity);

  const range = maxVal - minVal;
  const padding = range > 0
    ? range * 0.08
    : Math.max(Math.abs(maxVal) * 0.02, 1);

  minVal -= padding;
  maxVal += padding;

  return [minVal, maxVal];
};

class CustomizedAxisTick extends PureComponent {
  render() {
    const { x, y, payload, frequency } = this.props;
    const date = new Date(payload.value);

    let formattedDate;
    switch (frequency) {
      case 'minute':
        formattedDate = moment(date).format('MMM D, h:mm a');
        break;
      case 'hour':
        formattedDate = moment(date).format('MMM D, h A');
        break;
      case 'day':
      default:
        formattedDate = moment(date).format('MMM D');
        break;
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#cccccc"
          fontSize={10}
          transform="rotate(-10)"
        >
          {formattedDate}
        </text>
      </g>
    );
  }
}

export default function ResultsChart({
  data,
  frequency,
  isLoading,
  trendDirection,
  minimal = false,
  chartHeight,
  emptyTitle = 'No data for this timeframe.',
  emptyDescription = '',
}) {
  if (isLoading) {
    return (
      <div className="chart-loading fade-in">
        <Spinner
          animation="border"
          role="status"
          aria-label="Loading chart data"
          className="chart-loading-spinner"
        />
        <h3 style={{ color: '#cccccc', marginTop: '1rem' }}>Loading Chart Data...</h3>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="no-chart-data fade-in">
        <h3>{emptyTitle}</h3>
        {emptyDescription && <p>{emptyDescription}</p>}
      </div>
    );
  }

  // Decide portfolio line color
  let lineColor = '#4caf50'; // default green
  if (trendDirection === 'down') {
    lineColor = '#f44336';
  }

  const yDomain = getPortfolioValueDomain(data);

  const tooltipFormatter = (date) => moment(date).local().format('MMM D, YYYY h:mm a');

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const { portfolio_value, cash, spy_benchmark } = payload[0].payload || {};

      return (
        <Card className="tooltip-card fade-in">
          <Card.Body>
            {typeof portfolio_value === 'number' && (
              <Card.Title className="tooltip-title">
                <span style={{ color: lineColor }} className="chart-tooltip-name">
                  Portfolio Value:
                </span>
                &nbsp;
                <NumericFormat
                  value={portfolio_value}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  className="chart-tooltip-value"
                />
              </Card.Title>
            )}
            {typeof cash === 'number' && (
              <Card.Title className="tooltip-title mt-1">
                <span style={{ color: '#ffa500' }} className="chart-tooltip-name">
                  Cash:
                </span>
                &nbsp;
                <NumericFormat
                  value={cash}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  className="chart-tooltip-value"
                />
              </Card.Title>
            )}
            {typeof spy_benchmark === 'number' && (
              <Card.Title className="tooltip-title mt-1">
                <span style={{ color: '#999999' }} className="chart-tooltip-name">
                  Benchmark:
                </span>
                &nbsp;
                <NumericFormat
                  value={spy_benchmark}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  className="chart-tooltip-value"
                />
              </Card.Title>
            )}
            <Card.Text className="tooltip-text">{tooltipFormatter(label)}</Card.Text>
          </Card.Body>
        </Card>
      );
    }
    return null;
  };

  return (
    <div className="chart-container fade-in">
      <ResponsiveContainer width="100%" height={chartHeight || 400}>
        <LineChart
          data={data}
          margin={minimal ? { top: 12, right: 8, left: 8, bottom: 8 } : { top: 10, right: 10, left: 0, bottom: 20 }}
        >
          {!minimal && <CartesianGrid stroke="#333" vertical={false} horizontal={false} />}
          {!minimal && (
            <XAxis
              dataKey="date"
              tick={<CustomizedAxisTick frequency={frequency} />}
              type="number"
              scale="time"
              domain={['auto', 'auto']}
              stroke="#cccccc"
            />
          )}
          {minimal && <XAxis dataKey="date" type="number" scale="time" domain={['auto', 'auto']} hide />}
          <YAxis domain={yDomain} hide />
          <Tooltip content={<CustomTooltip />} />
          {!minimal && (
            <Legend
              verticalAlign="top"
              height={30}
              wrapperStyle={{ color: '#ffffff', fontSize: '0.85rem' }}
            />
          )}

          <Line
            type="monotone"
            dataKey="portfolio_value"
            name="Portfolio Value"
            stroke={lineColor}
            strokeWidth={4}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          {!minimal && (
            <Line
              type="monotone"
              dataKey="spy_benchmark"
              name="SPY (Benchmark)"
              stroke="#999999"
              strokeWidth={4}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
